import { inject, injectable } from "inversify";
import {
  ChatReplyModel,
  ChatSessionDetailModel,
  ChatSessionModel,
  FileParameter,
  ICPLAIChatClient,
  IMessageFeedbackModel,
  NewChatMessageModel,
  SetMessageFeedbackModel,
  UpdateChatSessionModel,
  UploadedChatDocumentModel,
} from "./api/cplapi/CPLAPI";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { rethrowAsError } from "./api/problemDetailsToError";

// A null chatSessionID starts a new chat
export interface IAIAssistantService {
  sendChatMessage(
    chatSessionID: string | null,
    message: string,
    runID: string,
    signal?: AbortSignal
  ): Promise<ChatReplyModel>;
  loadChatSession(chatSessionID: string): Promise<ChatSessionDetailModel>;
  deleteChatSession(chatSessionID: string): Promise<void>;
  getChatSessions(): Promise<ChatSessionModel[]>;
  renameChatSession(chatSessionID: string, newChatName: string): Promise<void>;
  uploadFile(chatSessionID: string | null, file: File): Promise<UploadedChatDocumentModel>;
  deleteChatDocument(documentID: string): Promise<void>;
  setMessageFeedback(messageID: number, feedback: IMessageFeedbackModel): Promise<void>;
  deleteMessageFeedback(messageID: number): Promise<void>;
}

@injectable()
export class AIAssistantService implements IAIAssistantService {
  @inject(SERVICE_TYPES.CPLAIChatClient) cplAIChatClient!: ICPLAIChatClient;

  async sendChatMessage(
    chatSessionID: string | null,
    message: string,
    runID: string,
    signal?: AbortSignal
  ): Promise<ChatReplyModel> {
    return this.cplAIChatClient
      .sendChatMessage(new NewChatMessageModel({ chatSessionID: chatSessionID ?? undefined, message, runID }), signal)
      .catch(rethrowAsError);
  }

  async loadChatSession(chatSessionID: string): Promise<ChatSessionDetailModel> {
    return this.cplAIChatClient.getChatSession(chatSessionID).catch(rethrowAsError);
  }

  async deleteChatSession(chatSessionID: string): Promise<void> {
    await this.cplAIChatClient.deleteChatSession(chatSessionID).catch(rethrowAsError);
  }

  async getChatSessions(): Promise<ChatSessionModel[]> {
    return this.cplAIChatClient.getChatSessions().catch(rethrowAsError);
  }

  async renameChatSession(chatSessionID: string, newChatName: string): Promise<void> {
    await this.cplAIChatClient
      .updateChatSession(new UpdateChatSessionModel({ chatSessionID, chatName: newChatName }))
      .catch(rethrowAsError);
  }

  async uploadFile(chatSessionID: string | null, file: File): Promise<UploadedChatDocumentModel> {
    const fileParam: FileParameter = { data: file, fileName: file.name };
    return await this.cplAIChatClient.uploadChatDocument({ chatSessionID, file: fileParam }).catch(rethrowAsError);
  }

  async deleteChatDocument(documentID: string): Promise<void> {
    await this.cplAIChatClient.deleteChatDocument(documentID).catch(rethrowAsError);
  }

  async setMessageFeedback(messageID: number, feedback: IMessageFeedbackModel): Promise<void> {
    await this.cplAIChatClient
      .setMessageFeedback(new SetMessageFeedbackModel({ messageID, ...feedback }))
      .catch(rethrowAsError);
  }

  async deleteMessageFeedback(messageID: number): Promise<void> {
    await this.cplAIChatClient.deleteMessageFeedback(messageID).catch(rethrowAsError);
  }
}
